import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from "@mui/icons-material/Inventory";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "../../api/axios";

const StockModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stockData, setStockData] = useState(null);

  const fetchStock = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/integrations/warehouse/stock");
      setStockData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch stock data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchStock();
    }
  }, [open]);

  const handleClose = () => {
    onClose();
    setStockData(null);
    setError(null);
  };

  const totalItems = stockData?.summary?.reduce((acc, item) => acc + item.count, 0) || 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: "#0D3B59",
          color: "white",
          py: 1.5,
        }}
      >
        <InventoryIcon />
        <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
          Airmaxx Stock
        </Typography>
        <IconButton
          onClick={fetchStock}
          disabled={loading}
          sx={{ color: "white", mr: 1 }}
          title="Refresh"
        >
          <RefreshIcon />
        </IconButton>
        <IconButton onClick={handleClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && stockData && (
          <>
            <Box sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Equipment Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total items in stock: <Chip label={totalItems} size="small" color="primary" sx={{ ml: 1 }} />
              </Typography>
            </Box>

            <Divider />

            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#e3f2fd" }}>Model</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#e3f2fd" }}>Quantity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockData.summary?.map((item) => (
                    <TableRow
                      key={item.model}
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                        "&:hover": { backgroundColor: "#e3f2fd" },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                          {item.model}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.count}
                          size="small"
                          color={item.count >= 10 ? "success" : item.count >= 5 ? "warning" : "error"}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {stockData.summary?.length === 0 && (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography color="text.secondary">No equipment in stock</Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StockModal;
